import { useQuery } from '@tanstack/react-query';
import { services as fallbackServices } from '../data/siteData.js';
import { api, unwrap } from '../services/api.js';

const fallbackBySlug = Object.fromEntries(fallbackServices.map((service) => [service.slug, service]));

const firstGalleryUrl = (gallery) => {
  if (!Array.isArray(gallery)) return '';
  const first = gallery.find((item) => (typeof item === 'string' ? item : item?.url));
  return typeof first === 'string' ? first : first?.url || '';
};

const normalizeGallery = (gallery) =>
  Array.isArray(gallery)
    ? gallery.filter((item) => (typeof item === 'string' ? item : item?.url))
    : [];

export const normalizePublicService = (service, { fromApi = true } = {}) => {
  const fallback = fallbackBySlug[service?.slug] || {};
  const gallery = normalizeGallery(service?.gallery);
  const fallbackGallery = normalizeGallery(fallback.gallery);

  if (fromApi) {
    return {
      ...service,
      key: service.key || fallback.key,
      icon: service.icon || fallback.icon,
      image: service.image || firstGalleryUrl(gallery),
      gallery,
    };
  }

  return {
    ...fallback,
    ...service,
    key: service?.key || fallback.key,
    icon: service?.icon || fallback.icon,
    image: service?.image || firstGalleryUrl(gallery) || fallback.image || firstGalleryUrl(fallbackGallery),
    gallery: gallery.length ? gallery : fallbackGallery,
  };
};

export const usePublicServices = () => {
  const query = useQuery({
    queryKey: ['public-services'],
    queryFn: () =>
      api
        .get('/services', {
          params: {
            status: 'published',
            sort: 'order',
          },
        })
        .then(unwrap),
    retry: false,
  });

  const hasApiPayload = Array.isArray(query.data);
  const sourceServices = hasApiPayload ? query.data : query.isPending ? fallbackServices : [];
  const services = sourceServices.map((service) => normalizePublicService(service, { fromApi: hasApiPayload }));

  return {
    ...query,
    services,
  };
};

export const usePublicService = (slug) => {
  const query = useQuery({
    queryKey: ['public-service', slug],
    queryFn: () =>
      api
        .get(`/services/${slug}`, {
          params: {
            status: 'published',
          },
        })
        .then(unwrap),
    enabled: Boolean(slug),
    retry: false,
  });
  const hasApiPayload = Boolean(query.data);
  const fallback = fallbackBySlug[slug];
  const service = hasApiPayload
    ? normalizePublicService(query.data, { fromApi: true })
    : query.isPending && fallback
      ? normalizePublicService(fallback, { fromApi: false })
      : null;

  return {
    ...query,
    service,
  };
};

export const localizedServiceField = (service, field, language) =>
  service?.[field]?.[language] || service?.[field]?.fr || service?.[field]?.en || '';

export const serviceTitle = (service, language, t) =>
  localizedServiceField(service, 'title', language) ||
  (service?.key ? t(`services.${service.key}.title`) : service?.slug || 'Service');

export const serviceSummary = (service, language, t) =>
  localizedServiceField(service, 'summary', language) ||
  (service?.key ? t(`services.${service.key}.summary`) : '');

export const serviceDescription = (service, language, t) =>
  localizedServiceField(service, 'description', language) ||
  (service?.key ? t(`services.${service.key}.description`) : '');

export const serviceBenefits = (service, language, t) => {
  if (service?.benefits?.length) {
    return service.benefits.map((item) => item?.[language] || item?.fr || item?.en).filter(Boolean);
  }

  const fallbackBenefits = service?.key ? t(`services.${service.key}.benefits`, { returnObjects: true }) : [];
  return Array.isArray(fallbackBenefits) ? fallbackBenefits : [];
};
